<script setup>
import { ref, onMounted } from "vue"
import { useToast } from "vue-toastification"

import CKEditor from "@ckeditor/ckeditor5-vue"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import "@ckeditor/ckeditor5-build-classic/build/translations/fa"

import Layout from "../../layouts/main.vue"
import PageHeader from "@/components/page-header"

import store from "../../state/store"
import usePersianTools from "../../use/usePersianTools"

// Get toast interface
const toast = useToast()

const { convertPersianToEnglish } = usePersianTools()

const title = "ویرایش تنظیمات"
const items = ref([
  {
    text: "داشبورد",
    href: "/",
  },
  {
    text: "ویرایش تنظیمات",
    active: true,
  },
])

const settings = ref([])
const settingsId = ref(1)
const errors = ref({})
const loading = ref(false)

const onFileChange = (event) => {
  settings.value.application_logo = event.target.files[0]
}

/**
 *! start editor
 */

const editorConfig = {
  language: "fa",
  toolbar: [
    "undo",
    "redo",
    "|",
    "heading",
    "|",
    "bold",
    "italic",
    "-", // break point
    "|",
    "alignment",
    "link",
    "|",
    "bulletedList",
    "numberedList",
  ],
  ckfinder: {
    options: {
      language: "fa",
    },
  },
}

const editor = ClassicEditor
const ckeditor = CKEditor.component

/**
 *! end editor
 */

onMounted(async () => {
  const { data } = await store.dispatch("getSettings", settingsId.value)
  settings.value = {
    ...data.data,
  }
})

const onSubmit = () => {
  loading.value = true
  store
    .dispatch("updateSettings", {
      settings,
    })
    .then((response) => {
      loading.value = false
      if (response.status === 200) {
        toast.success("تنظیمات با موفقیت به روزرسانی شد!", {
          timeout: 3000,
        })

        location.reload()
      }
    })
    .catch((err) => {
      loading.value = false
      errors.value = err.response.data.errors
    })
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <b-overlay
      variant="white"
      opacity="0.75"
      blur="2px"
      :show="loading"
      spinner-variant="success"
      rounded="sm"
    >
      <b-row>
        <b-col lg="8">
          <form @submit.prevent="onSubmit" enctype="multipart/form-data">
            <b-card no-body>
              <b-card-body>
                <div class="mb-3">
                  <label class="form-label" for="application_name"
                    >نام اپلیکیشن</label
                  >
                  <input
                    type="text"
                    class="form-control"
                    id="application_name"
                    placeholder="نام اپلیکیشن را وارد کنید"
                    v-model="settings.application_name"
                  />
                  <div v-if="errors">
                    <div
                      class="invalid-feedback"
                      v-if="errors.application_name"
                    >
                      <span>{{ errors.application_name[0] }}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="form-label">متن درباره ما</label>
                  <ckeditor
                    v-model="settings.about_us"
                    :editor="editor"
                    :config="editorConfig"
                  ></ckeditor>
                </div>
              </b-card-body>
            </b-card>
            <b-card no-body>
              <b-card-body>
                <div>
                  <label class="form-label">متن تماس با ما</label>
                  <ckeditor
                    v-model="settings.contact_us"
                    :editor="editor"
                    :config="editorConfig"
                  ></ckeditor>
                </div>
              </b-card-body>
            </b-card>
            <b-card no-body>
              <b-card-body>
                <div>
                  <label class="form-label">متن قوانین و مقررات</label>
                  <ckeditor
                    v-model="settings.terms_conditions"
                    :editor="editor"
                    :config="editorConfig"
                  ></ckeditor>
                </div>
              </b-card-body>
            </b-card>

            <b-card no-body>
              <b-card-header>
                <h5 class="card-title mb-0">لوگو اپلیکیشن</h5>
              </b-card-header>
              <b-card-body>
                <div class="mb-4">
                  <p class="text-muted">لوگو اپلیکیشن را بارگذاری کنید.</p>
                  <input
                    class="form-control"
                    id="application-logo-input"
                    type="file"
                    accept="image/png, image/gif, image/jpeg"
                    @change="onFileChange"
                  />
                  <img
                    class="img-thumbnail my-2"
                    :alt="settings.application_name"
                    width="150"
                    :src="settings.application_logo"
                    data-holder-rendered="true"
                  />
                  <div v-if="errors">
                    <div
                      class="invalid-feedback"
                      v-if="errors.application_logo"
                    >
                      <span>{{ errors.application_logo[0] }}</span>
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>
            <b-card no-body>
              <b-card-header>
                <ul
                  class="nav nav-tabs-custom card-header-tabs border-bottom-0"
                  role="tablist"
                >
                  <li class="nav-item">
                    <b-link
                      class="nav-link active"
                      data-bs-toggle="tab"
                      href="#editsettings-general-info"
                      role="tab"
                    >
                      تنظیمات
                    </b-link>
                  </li>
                </ul>
              </b-card-header>
              <b-card-body>
                <div class="tab-content">
                  <div
                    class="tab-pane active"
                    id="editsettings-general-info"
                    role="tabpanel"
                  >
                    <b-row>
                      <b-col>
                        <b-row>
                          <b-col sm="6">
                            <div class="mb-3">
                              <label
                                class="form-label"
                                for="referral_code_recharge"
                                >مبلغ شارژ معرف</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control"
                                  id="referral_code_recharge"
                                  placeholder="مبلغ شارژ معرف را وارد کنید"
                                  v-model="settings.referral_code_recharge"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'referral_code_recharge'
                                    )
                                  "
                                />
                              </div>
                              <div
                                class="invalid-feedback text-primary text-opacity-75"
                              >
                                <span>{{
                                  settings.referral_code_recharge_words
                                }}</span>
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.referral_code_recharge"
                                >
                                  <span>{{
                                    errors.referral_code_recharge[0]
                                  }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                          <b-col sm="6">
                            <div class="mb-3">
                              <label class="form-label" for="shipping_cost"
                                >هزینه ارسال</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control"
                                  id="shipping_cost"
                                  placeholder="هزینه ارسال را وارد کنید"
                                  v-model="settings.shipping_cost"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'shipping_cost'
                                    )
                                  "
                                />
                              </div>
                              <div
                                class="invalid-feedback text-primary text-opacity-75"
                              >
                                <span>{{ settings.shipping_cost_words }}</span>
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.shipping_cost"
                                >
                                  <span>{{ errors.shipping_cost[0] }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-row>
                          <b-col sm="6">
                            <div class="mb-3">
                              <label
                                class="form-label"
                                for="minimum_order_amount"
                                >حداقل مبلغ سفارش</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control"
                                  id="minimum_order_amount"
                                  placeholder=" حداقل مبلغ سفارش را وارد کنید"
                                  v-model="settings.minimum_order_amount"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'minimum_order_amount'
                                    )
                                  "
                                />
                              </div>
                              <div
                                class="invalid-feedback text-primary text-opacity-75"
                              >
                                <span>{{
                                  settings.minimum_order_amount_words
                                }}</span>
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.minimum_order_amount"
                                >
                                  <span>{{
                                    errors.minimum_order_amount[0]
                                  }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                          <b-col sm="6">
                            <div class="mb-3">
                              <label
                                class="form-label"
                                for="maximum_order_amount"
                                >حداکثر مبلغ سفارش</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control"
                                  id="maximum_order_amount"
                                  placeholder="  حداکثر مبلغ سفارش را وارد کنید"
                                  v-model="settings.maximum_order_amount"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'maximum_order_amount'
                                    )
                                  "
                                />
                              </div>
                              <div
                                class="invalid-feedback text-primary text-opacity-75"
                              >
                                <span>{{
                                  settings.maximum_order_amount_words
                                }}</span>
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.maximum_order_amount"
                                >
                                  <span>{{
                                    errors.maximum_order_amount[0]
                                  }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <b-row>
                          <b-col sm="4">
                            <div class="mb-3">
                              <label class="form-label" for="tax_percentage"
                                >مالیات بر ارزش افزوده (درصد)</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="number"
                                  class="form-control"
                                  id="tax_percentage"
                                  placeholder="درصد مالیات را وارد کنید"
                                  v-model="settings.tax_percentage"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'tax_percentage'
                                    )
                                  "
                                />
                              </div>
                              <div
                                class="invalid-feedback text-primary text-opacity-75"
                              >
                                <span>{{ settings.tax_percentage_words }}</span>
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.tax_percentage"
                                >
                                  <span>{{ errors.tax_percentage[0] }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                          <b-col sm="4">
                            <div class="mb-3">
                              <label
                                class="form-label"
                                for="support_phone_number"
                                >شماره تلفن پشتیبانی</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control text-end"
                                  id="support_phone_number"
                                  placeholder="02112345678"
                                  v-model="settings.support_phone_number"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'support_phone_number'
                                    )
                                  "
                                />
                              </div>
                              <div v-if="errors">
                                <div
                                  class="invalid-feedback"
                                  v-if="errors.support_phone_number"
                                >
                                  <span>{{
                                    errors.support_phone_number[0]
                                  }}</span>
                                </div>
                              </div>
                            </div>
                          </b-col>
                          <b-col sm="4">
                            <div class="mb-3">
                              <label class="form-label" for="app_version"
                                >نسخه اپلیکیشن</label
                              >
                              <div class="input-group mb-3">
                                <input
                                  type="text"
                                  class="form-control text-end"
                                  id="app_version"
                                  placeholder="1, 2, 3, ..."
                                  dir="ltr"
                                  v-model="settings.app_version"
                                  @input="
                                    convertPersianToEnglish(
                                      $event.target.value,
                                      settings,
                                      'app_version'
                                    )
                                  "
                                />
                              </div>
                            </div>
                          </b-col>
                        </b-row>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </b-card-body>
            </b-card>
            <div class="text-end mb-3">
              <b-button type="submit" variant="success" class="w-sm"
                >ویرایش</b-button
              >
            </div>
          </form>
        </b-col>

        <b-col lg="4">
          <b-card no-body>
            <b-card-header>
              <h5 class="card-title mb-0">متن به روزرسانی</h5>
            </b-card-header>
            <b-card-body>
              <textarea
                class="form-control"
                id="update_text"
                placeholder="متن به روز رسانی را وارد کنید"
                rows="3"
                v-model="settings.update_text"
              ></textarea>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </Layout>
</template>
